import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Shield, Newspaper, Calendar, Database, Sparkles, CheckCircle2 } from 'lucide-react';

/**
 * PoweredByBadges - Shows verification badges on generated answers
 * Displays the AI features powering the answer generation
 */
export const PoweredByBadges = ({ ragEnhanced = false, showAll = false }) => {
  return (
    <div className="flex flex-wrap items-center gap-2 py-2 px-3 bg-gradient-to-r from-slate-50 to-indigo-50 rounded-lg border border-slate-200">
      <span className="text-xs font-medium text-slate-500 mr-1">Powered by:</span>
      
      {ragEnhanced && (
        <Badge variant="outline" className="bg-emerald-50 border-emerald-300 text-emerald-700 text-xs gap-1">
          <Database className="w-3 h-3" />
          RAG Enhanced
        </Badge>
      )}
      
      <Badge variant="outline" className="bg-blue-50 border-blue-300 text-blue-700 text-xs gap-1">
        <Newspaper className="w-3 h-3" />
        GNews Live
      </Badge>
      
      <Badge variant="outline" className="bg-purple-50 border-purple-300 text-purple-700 text-xs gap-1">
        <Calendar className="w-3 h-3" />
        2025 Updated
      </Badge>
      
      <Badge variant="outline" className="bg-amber-50 border-amber-300 text-amber-700 text-xs gap-1">
        <Shield className="w-3 h-3" />
        Data Verified
      </Badge>
      
      {showAll && (
        <Badge variant="outline" className="bg-pink-50 border-pink-300 text-pink-700 text-xs gap-1">
          <Sparkles className="w-3 h-3" />
          Zero Hallucination
        </Badge>
      )}
    </div>
  );
};

/**
 * VerificationBadge - Single badge showing verification status
 */
export const VerificationBadge = ({ type = 'verified' }) => {
  const badges = {
    verified: {
      icon: CheckCircle2,
      text: 'Verified Data',
      className: 'bg-emerald-100 text-emerald-700 border-emerald-300'
    },
    gnews: {
      icon: Newspaper,
      text: 'GNews Enhanced',
      className: 'bg-blue-100 text-blue-700 border-blue-300'
    },
    updated: {
      icon: Calendar,
      text: '2025 Current',
      className: 'bg-purple-100 text-purple-700 border-purple-300'
    },
    rag: {
      icon: Database,
      text: 'RAG Powered',
      className: 'bg-indigo-100 text-indigo-700 border-indigo-300'
    }
  };
  
  const badge = badges[type] || badges.verified;
  const Icon = badge.icon;
  
  return (
    <Badge variant="outline" className={`text-xs gap-1 ${badge.className}`}>
      <Icon className="w-3 h-3" />
      {badge.text}
    </Badge>
  );
};

/**
 * DataSourceIndicator - Shows where data comes from
 */
export const DataSourceIndicator = ({ sources = [] }) => {
  const defaultSources = ['NFHS', 'Census', 'Economic Survey', 'PIB'];
  const displaySources = sources.length > 0 ? sources : defaultSources;
  
  return (
    <div className="flex items-center gap-2 text-xs text-slate-500">
      <Shield className="w-3 h-3 text-emerald-500" />
      <span>Data from:</span>
      <div className="flex gap-1 flex-wrap">
        {displaySources.slice(0, 3).map((source, idx) => (
          <span key={idx} className="bg-slate-100 px-1.5 py-0.5 rounded text-slate-600">
            {source}
          </span>
        ))}
        {displaySources.length > 3 && (
          <span className="text-slate-400">+{displaySources.length - 3} more</span>
        )}
      </div>
    </div>
  );
};

export default PoweredByBadges;
